import { MobXProviderContext } from "mobx-react";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { confirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { FileUpload } from "primereact/fileupload";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getErrorTitle, JOB_STATUS, RESIDENT_ASSESSMENT_MAX_FILE_SIZE_IN_BYTES, RESIDENT_CARE_PLAN_TYPE } from "../../../constants/constants";
import { BODY_CHART_CARE_PLAN_TYPE } from "../../../constants/carePlanBodyChart";
import { RouterAddress } from "../../../constants/RouterAddress";
import { ROLE_TYPE_MHP, ROLE_TYPE_NURSE, ROLE_TYPE_PROVIDER } from "../../../constants/RegisterConstants";
import {
    DEFAULT_ACTIVITY_SECTION,
    DEFAULT_BODY_CHART_SECTION,
    DEFAULT_COMMUNICATION_SECTION,
    DEFAULT_HEALTH_INDICATOR_SECTION,
    DEFAULT_INSTRUMENTAL_ACTIVITY_SECTION,
    DEFAULT_LEFT_ALONE_SECTION,
    DEFAULT_MEDICATION_SECTION,
    DEFAULT_PSYCH_SOCIAL_SECTION,
    DEFAULT_SUMMARY_SECTION,
    DEFAULT_TREATMENT_SECTION,
    DEFAULT_UNIVERSAL_PRECAUTIONS_SECTION,
} from "../../../constants/carePlanConstants";
import { Binder, BinderDocument, BinderFolder } from "../../../models/binder";
import {
    ensureResidentBinderBundle,
    getBinderFolderByCode,
    getFolderDocuments,
    getLatestBinderDocumentVersion,
    getPrimaryDocument,
    RESIDENT_BINDER_FOLDER_CODES,
    upsertBinderDocumentWithVersion,
    uploadNewBinderDocument,
} from "../../../utils/binderFeatureUtils";
import { dashboardPageContainerStyle, renderDashboardHeader } from "../../dashboard/pages/dashboardStyles";
import {
    appConfirmDialogClasses,
    appDialogContentStyle,
    appDialogHeaderStyle,
    appDialogStyle,
    appMutedTextStyle,
    appNeutralPanelStyle,
    appSectionCardStyle,
    renderConfirmMessage,
    renderSurfaceHeader,
} from "../../../components/general/uiSurfaceStyles";
import { openUrlInNewTab, sanitizeRichHtml } from "../../../utils/securityUtils";
import { capitalizeFirstLetter } from "../../../utils/stringUtils";
import SummarySection from "../careplan/summarySection";
import BodyChartSection from "../careplan/bodyChartSection";
import ResponsiblePartiesSection from "../careplan/responsiblePartiesSection";
import CommunicationSection from "../careplan/communicationSection";
import MedicationManagementSection from "../careplan/medicationManagementSection";
import HealthIndicatorSection from "../careplan/healthIndicatorSection";
import TreatmentProgramSection from "../careplan/treatmentProgramSection";
import PsychSocialSection from "../careplan/psychSocialSection";
import LeftAloneSection from "../careplan/leftAloneSection";
import UniversalPrecautionSection from "../careplan/universalPrecautionSection";
import ActivitiesOfDailyLivingSection from "../careplan/activitiesOfDailyLivingSection";
import InstrumentalActivitiesSection from "../careplan/instrumentalActivitiesSection";

const ASSESSMENT_UPLOAD_ACCEPT = ".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const ASSESSMENT_UPLOAD_ALLOWED_EXTENSIONS = ["pdf", "doc", "docx"];

const NCP_STEPS: { label: string; icon: string }[] = [
    { label: RESIDENT_CARE_PLAN_TYPE.SUMMARY, icon: "pi pi-file-edit" },
    { label: RESIDENT_CARE_PLAN_TYPE.RESPONSIBLE_PARTIES, icon: "pi pi-users" },
    { label: RESIDENT_CARE_PLAN_TYPE.COMMUNICATION, icon: "pi pi-comments" },
    { label: RESIDENT_CARE_PLAN_TYPE.MEDICATION_MANAGEMENT, icon: "pi pi-heart" },
    { label: RESIDENT_CARE_PLAN_TYPE.HEALTH_INDICATORS, icon: "pi pi-chart-line" },
    { label: BODY_CHART_CARE_PLAN_TYPE, icon: "pi pi-map" },
    { label: RESIDENT_CARE_PLAN_TYPE.TREATMENT_PROGRAM_THERAPIES, icon: "pi pi-list-check" },
    { label: RESIDENT_CARE_PLAN_TYPE.PSYCH_SOCIAL_COGNITIVE, icon: "pi pi-brain" },
    { label: RESIDENT_CARE_PLAN_TYPE.LEFT_ALONE_STATUS, icon: "pi pi-user" },
    { label: RESIDENT_CARE_PLAN_TYPE.UNIVERSAL_PRECAUTIONS, icon: "pi pi-shield" },
    { label: RESIDENT_CARE_PLAN_TYPE.ACTIVITIES_OF_DAILY_LIVING, icon: "pi pi-home" },
    { label: RESIDENT_CARE_PLAN_TYPE.INSTRUMENTAL_ACTIVITIES_OF_DAILY_LIVING, icon: "pi pi-wrench" },
];

const DEFAULT_CARE_PLAN_SECTIONS: Record<string, any> = {
    [RESIDENT_CARE_PLAN_TYPE.SUMMARY]: DEFAULT_SUMMARY_SECTION,
    [RESIDENT_CARE_PLAN_TYPE.RESPONSIBLE_PARTIES]: {
        carePlanType: RESIDENT_CARE_PLAN_TYPE.RESPONSIBLE_PARTIES,
        collateralContacts: [],
    },
    [RESIDENT_CARE_PLAN_TYPE.COMMUNICATION]: DEFAULT_COMMUNICATION_SECTION,
    [RESIDENT_CARE_PLAN_TYPE.MEDICATION_MANAGEMENT]: DEFAULT_MEDICATION_SECTION,
    [RESIDENT_CARE_PLAN_TYPE.HEALTH_INDICATORS]: DEFAULT_HEALTH_INDICATOR_SECTION,
    [BODY_CHART_CARE_PLAN_TYPE]: DEFAULT_BODY_CHART_SECTION,
    [RESIDENT_CARE_PLAN_TYPE.TREATMENT_PROGRAM_THERAPIES]: DEFAULT_TREATMENT_SECTION,
    [RESIDENT_CARE_PLAN_TYPE.PSYCH_SOCIAL_COGNITIVE]: DEFAULT_PSYCH_SOCIAL_SECTION,
    [RESIDENT_CARE_PLAN_TYPE.LEFT_ALONE_STATUS]: DEFAULT_LEFT_ALONE_SECTION,
    [RESIDENT_CARE_PLAN_TYPE.UNIVERSAL_PRECAUTIONS]: DEFAULT_UNIVERSAL_PRECAUTIONS_SECTION,
    [RESIDENT_CARE_PLAN_TYPE.ACTIVITIES_OF_DAILY_LIVING]: DEFAULT_ACTIVITY_SECTION,
    [RESIDENT_CARE_PLAN_TYPE.INSTRUMENTAL_ACTIVITIES_OF_DAILY_LIVING]: DEFAULT_INSTRUMENTAL_ACTIVITY_SECTION,
};

const renderAccordionHeader = (index: number, label: string, icon: string, activeStep: number | null) => (
    <span style={{ display: "flex", alignItems: "center", gap: "0.625rem", fontWeight: 600, fontSize: "0.9rem" }}>
        <span style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            width: 22, height: 22, borderRadius: "50%",
            background: index === activeStep ? "#1d4ed8" : "#94a3b8",
            color: "#fff", fontSize: "0.68rem", fontWeight: 700, flexShrink: 0,
        }}>
            {String(index + 1).padStart(2, "0")}
        </span>
        <i className={icon} style={{ color: index === activeStep ? "#1d4ed8" : "#64748b", marginLeft: 2 }} />
        {capitalizeFirstLetter(label)}
    </span>
);

const getNcpChooseOptions = () => ({
    icon: "pi pi-fw pi-images",
    label: "Choose File",
    className: "custom-choose-btn p-button-rounded p-button-outlined",
    style: {
        background: "#f3ca38",
        borderColor: "#f3ca38",
        color: "#ffffff",
        fontWeight: 700,
        boxShadow: "0 12px 28px rgba(30, 64, 175, 0.22)",
    },
});

const getNcpUploadOptions = (shouldPulse: boolean) => ({
    icon: "pi pi-fw pi-cloud-upload",
    label: "Upload and start draft",
    className: `custom-upload-btn p-button-rounded p-button-outlined ${shouldPulse ? " resident-upload-btn-pulse" : ""}`,
    style: {
        background: "#f29913",
        borderColor: "#f3ca38",
        color: "#ffffff",
        fontWeight: 700,
        boxShadow: "0 12px 28px rgba(243, 202, 56, 0.24)",
    },
});

const NCP_CANCEL_OPTIONS = {
    icon: "pi pi-fw pi-times",
    className: "custom-cancel-btn p-button-rounded p-button-outlined p-button-danger",
};

const nurseNcpHelpHtmlStyle = `
    .nurse-ncp-help-html {
        color: #475569;
        line-height: 1.7;
    }
    .nurse-ncp-help-html p,
    .nurse-ncp-help-html div {
        margin: 0 0 0.85rem 0;
    }
    .nurse-ncp-help-html ul,
    .nurse-ncp-help-html ol {
        margin: 0.35rem 0 1rem 1.25rem;
        padding-left: 0.9rem;
    }
    .nurse-ncp-help-html li {
        margin-bottom: 0.45rem;
    }
    .nurse-ncp-help-html strong,
    .nurse-ncp-help-html b {
        color: #0f172a;
    }
    .nurse-ncp-help-html br {
        display: block;
        content: "";
        margin-top: 0.45rem;
    }
`;

const NurseResidentNcpPage = () => {
    const { residentId, homeId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { backendAPIStore, authStore } = useContext(MobXProviderContext);
    const toast = useRef<Toast>(null);
    const autoSaveHideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [loadingPage, setLoadingPage] = useState(true);
    const [savingCarePlan, setSavingCarePlan] = useState(false);
    const [fieldAutoSaveIndicator, setFieldAutoSaveIndicator] = useState<{
        visible: boolean;
        top: number;
        left: number;
        message: string;
        state: "saving" | "saved";
    }>({
        visible: false,
        top: 0,
        left: 0,
        message: "",
        state: "saved",
    });
    const [creatingVersion, setCreatingVersion] = useState(false);
    const attemptedAutoCreateInitialVersionRef = useRef(false);
    const [generatingDocument, setGeneratingDocument] = useState(false);
    const [generatingBehaviorInterventionsDocument, setGeneratingBehaviorInterventionsDocument] = useState(false);
    const [generatingPreliminaryServicePlanDocument, setGeneratingPreliminaryServicePlanDocument] = useState(false);
    const [residentProfile, setResidentProfile] = useState<any | null>(null);
    const [forms, setForms] = useState<any[]>([]);
    const [currentFormId, setCurrentFormId] = useState("");
    const [currentFormHeader, setCurrentFormHeader] = useState<any | null>(null);
    const [residentBinder, setResidentBinder] = useState<Binder | null>(null);
    const [residentBinderFolders, setResidentBinderFolders] = useState<BinderFolder[]>([]);
    const [residentBinderDocuments, setResidentBinderDocuments] = useState<BinderDocument[]>([]);
    const [sharedResidentBinderAccessDenied, setSharedResidentBinderAccessDenied] = useState(false);
    const [selectedBinderFolderId, setSelectedBinderFolderId] = useState("");
    const [selectedBinderAssessmentDocumentId, setSelectedBinderAssessmentDocumentId] = useState("");
    const [selectedAssessmentFile, setSelectedAssessmentFile] = useState<File | null>(null);
    const [uploadingAssessment, setUploadingAssessment] = useState(false);
    const [processingAssessment, setProcessingAssessment] = useState(false);
    const [assessmentDialogError, setAssessmentDialogError] = useState<{ summary: string; detail: string } | null>(null);
    const [assessmentJobDetails, setAssessmentJobDetails] = useState<any | null>(null);
    const [carePlanSections, setCarePlanSections] = useState<Record<string, any>>(DEFAULT_CARE_PLAN_SECTIONS);
    const [showAssessmentImportDialog, setShowAssessmentImportDialog] = useState(false);
    const [activeStep, setActiveStep] = useState<number | null>(0);
    const [showHelpDialog, setShowHelpDialog] = useState(false);
    const [helpMessage, setHelpMessage] = useState("");
    const [showFeatureAccessDialog, setShowFeatureAccessDialog] = useState(false);
    const [featureAccessDialogMessage, setFeatureAccessDialogMessage] = useState("");
    const [featureAccessDialogAllowsCreditUse, setFeatureAccessDialogAllowsCreditUse] = useState(false);
    const [isUserSubscribed, setIsUserSubscribed] = useState(false);
    const [automationCreditsAvailable, setAutomationCreditsAvailable] = useState(0);
    const [assessmentElapsedTick, setAssessmentElapsedTick] = useState(0);
    const featureAccessRetryActionRef = useRef<null | (() => void)>(null);
    const ACTIVE_NCP_JOB_STATUSES = useMemo(() => new Set([
        JOB_STATUS.SUBMITTED,
        JOB_STATUS.IN_PROGRESS,
        "RETRYING",
    ]), []);

    const isProviderNurseResidentMode = location.pathname.startsWith("/provider/nurse/");
    const isSharedResidentMode = location.pathname.startsWith("/shared/home/") && location.pathname.includes("/careplan/");
    const requiresProviderSubscriptionMessageOnly = isSharedResidentMode;
    const userRoleType = authStore.getUserRoleType();
    const sharedDashboardPath = userRoleType === ROLE_TYPE_MHP
        ? RouterAddress.MHP_HOME
        : isProviderNurseResidentMode
            ? RouterAddress.PROVIDER_NURSE_HOME
            : RouterAddress.NURSE_HOME;
    const backToResidentPath = residentId
        ? (isSharedResidentMode
            ? sharedDashboardPath
            : isProviderNurseResidentMode
            ? RouterAddress.getProviderNurseResidentProfilePagePath(residentId)
            : RouterAddress.getNurseResidentProfilePagePath(residentId))
        : RouterAddress.NURSE_HOME;
    const selectedFormIdFromUrl = new URLSearchParams(location.search).get("formId") || "";
    const binderPath = residentId
        ? (isSharedResidentMode
            ? RouterAddress.getSharedResidentBinderPagePath(homeId || "", residentId)
            : isProviderNurseResidentMode
            ? RouterAddress.getProviderNurseResidentBinderPagePath(residentId)
            : RouterAddress.getNurseResidentBinderPagePath(residentId))
        : RouterAddress.NURSE_HOME;
    const delegationPath = residentId
        ? (isSharedResidentMode
            ? RouterAddress.getSharedResidentDelegationPagePath(homeId || "", residentId)
            : isProviderNurseResidentMode
            ? RouterAddress.getProviderNurseResidentDelegationPagePath(residentId)
            : RouterAddress.getNurseResidentDelegationPagePath(residentId))
        : RouterAddress.NURSE_HOME;
    const chooseOptions = useMemo(() => getNcpChooseOptions(), []);
    const uploadOptions = useMemo(
        () => getNcpUploadOptions(!!selectedAssessmentFile && !uploadingAssessment && !processingAssessment),
        [processingAssessment, selectedAssessmentFile, uploadingAssessment]
    );

    const isSubscriptionRequiredError = (error: any) => {
        const code = String(error?.code || error?.cause?.message || error?.details?.code || "").trim().toUpperCase();
        const message = String(error?.message || error?.details?.error || "").toLowerCase();
        return code === "SUBSCRIPTION_REQUIRED"
            || code === "SUBSCRIPTION_LIMIT_REACHED"
            || message.includes("active subscription is required")
            || message.includes("review billing to continue")
            || message.includes("automation credits")
            || message.includes("subscribe or buy automation credits");
    };

    const openFeatureAccessDialog = (options?: {
        allowCreditUse?: boolean;
        onUseCredit?: () => void;
        customMessage?: string;
    }) => {
        if (requiresProviderSubscriptionMessageOnly) {
            featureAccessRetryActionRef.current = null;
            setFeatureAccessDialogAllowsCreditUse(false);
            setFeatureAccessDialogMessage("The provider must have an active subscription to use the shared resident care plan workspace.");
            setShowFeatureAccessDialog(true);
            return;
        }
        const canUseCreditNow = !!options?.allowCreditUse && automationCreditsAvailable > 0;
        featureAccessRetryActionRef.current = canUseCreditNow ? (options?.onUseCredit || null) : null;
        setFeatureAccessDialogAllowsCreditUse(canUseCreditNow);

        if (options?.customMessage) {
            setFeatureAccessDialogMessage(options.customMessage);
        } else if (isUserSubscribed) {
            setFeatureAccessDialogMessage(
                canUseCreditNow
                    ? "Use 1 automation credit to continue."
                    : "Buy automation credits to continue."
            );
        } else {
            setFeatureAccessDialogMessage(
                canUseCreditNow
                    ? "Use 1 automation credit to continue, or subscribe for monthly access."
                    : "Subscribe or buy automation credits to continue."
            );
        }
        setShowFeatureAccessDialog(true);
    };

    const loadForms = async (selectedFormId?: string) => {
        if (!residentId) return;

        const response: any = await backendAPIStore.getNurseNcpForms(residentId);
        const nextForms = response?.forms || [];
        const nextFormId = selectedFormId || currentFormId || response?.activeFormId || nextForms[0]?.formId || "";
        setForms(nextForms);
        setCurrentFormId(nextFormId);
        return nextFormId;
    };

    const applyDefaultBinderAssessmentSelection = (folders: BinderFolder[], documents: BinderDocument[]) => {
        const assessmentFolder = getBinderFolderByCode(folders, RESIDENT_BINDER_FOLDER_CODES.ASSESSMENT);
        const foldersWithFiles = folders.filter((folder) => getFolderDocuments(documents, folder.folderId).length > 0);
        const defaultFolder = foldersWithFiles.find((folder) => folder.folderId === assessmentFolder?.folderId) || foldersWithFiles[0];
        const defaultFolderDocuments = getFolderDocuments(documents, defaultFolder?.folderId);
        const primaryDocument = getPrimaryDocument(defaultFolderDocuments);
        setSelectedBinderFolderId(defaultFolder?.folderId || "");
        setSelectedBinderAssessmentDocumentId(primaryDocument?.binderDocumentId || defaultFolderDocuments[0]?.binderDocumentId || "");
    };

    const loadResidentBinderData = async () => {
        if (!residentId) return;
        try {
            const bundle = await ensureResidentBinderBundle(backendAPIStore, residentId);
            setSharedResidentBinderAccessDenied(false);
            setResidentBinder(bundle.binder);
            setResidentBinderFolders(bundle.folders);
            setResidentBinderDocuments(bundle.documents);
            applyDefaultBinderAssessmentSelection(bundle.folders, bundle.documents);
        } catch (error: any) {
            const normalizedMessage = String(error?.message || "").trim().toLowerCase();
            if (isSharedResidentMode && normalizedMessage.includes("permission denied")) {
                setSharedResidentBinderAccessDenied(true);
                setResidentBinder(null);
                setResidentBinderFolders([]);
                setResidentBinderDocuments([]);
                setSelectedBinderAssessmentDocumentId("");
                return;
            }
            throw error;
        }
    };

    const loadFormRecords = async (formId: string) => {
        if (!formId) {
            setCurrentFormHeader(null);
            setCarePlanSections(DEFAULT_CARE_PLAN_SECTIONS);
            return;
        }

        const records: any[] = await backendAPIStore.getNurseNcpForms(undefined, formId);
        const header = records.find((item: any) => item.recordType === "HEADER") || null;
        const nextSections = { ...DEFAULT_CARE_PLAN_SECTIONS };

        records
            .filter((item: any) => String(item?.recordType || "").startsWith("SECTION#"))
            .forEach((item: any) => {
                const carePlanType = String(item?.carePlanType || "").trim();
                if (!carePlanType) return;
                nextSections[carePlanType] = item;
            });

        setCurrentFormHeader(header);
        setCarePlanSections(nextSections);
    };

    const hasActiveNcpImportJob = useMemo(() => {
        if (processingAssessment) {
            return true;
        }

        const normalizedCurrentJobStatus = String(
            assessmentJobDetails?.jobStatus
            || currentFormHeader?.jobStatus
            || "",
        ).trim().toUpperCase();

        return normalizedCurrentJobStatus ? ACTIVE_NCP_JOB_STATUSES.has(normalizedCurrentJobStatus) : false;
    }, [ACTIVE_NCP_JOB_STATUSES, assessmentJobDetails?.jobStatus, currentFormHeader?.jobStatus, processingAssessment]);

    useEffect(() => {
        const loadPage = async () => {
            if (!residentId) return;
            setLoadingPage(true);
            try {
                await backendAPIStore.init();
                const resolvedUserId = authStore.getUserId();
                if (resolvedUserId) {
                    const billingDetails: any = await backendAPIStore.getSubscription(
                        resolvedUserId,
                        isProviderNurseResidentMode ? ROLE_TYPE_PROVIDER : ROLE_TYPE_NURSE,
                    );
                    setIsUserSubscribed(!!billingDetails?.isSubscribed);
                    setAutomationCreditsAvailable(
                        Number(
                            billingDetails?.automationCreditsAvailable
                            || billingDetails?.purchasedAutomationCreditsAvailable
                            || billingDetails?.featureCreditBalances?.ncp_generation
                            || 0,
                        ),
                    );
                }
                const profile = (isProviderNurseResidentMode || isSharedResidentMode)
                    ? await backendAPIStore.getProviderResidentProfile(residentId)
                    : await backendAPIStore.getNurseResidentProfile(residentId);
                setResidentProfile(profile || null);
                const nextFormId = await loadForms(selectedFormIdFromUrl || undefined);
                await loadResidentBinderData();
                if (nextFormId) {
                    await loadFormRecords(nextFormId);
                }
            } catch (error: any) {
                toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to load nurse care plan.", life: 4000 });
            } finally {
                setLoadingPage(false);
            }
        };

        void loadPage();
    }, [authStore, backendAPIStore, isProviderNurseResidentMode, isSharedResidentMode, residentId, selectedFormIdFromUrl]);

    useEffect(() => {
        if (!currentFormId) return;
        void loadFormRecords(currentFormId);
    }, [currentFormId]);

    useEffect(() => {
        const getIndicatorTarget = (fieldName?: string) => {
            if (fieldName) {
                const byId = document.getElementById(fieldName);
                if (byId) {
                    return byId as HTMLElement;
                }

                const byName = document.querySelector(`[name="${fieldName}"]`) as HTMLElement | null;
                if (byName) {
                    return byName;
                }
            }

            return document.activeElement instanceof HTMLElement ? document.activeElement : null;
        };

        const handleAutoSaveStatus = (event: Event) => {
            const customEvent = event as CustomEvent<{
                state: "saving" | "saved";
                fieldName?: string;
                timestamp?: string;
            }>;
            const target = getIndicatorTarget(customEvent.detail.fieldName);
            const rect = target?.getBoundingClientRect();

            if (autoSaveHideTimeoutRef.current) {
                clearTimeout(autoSaveHideTimeoutRef.current);
            }

            setFieldAutoSaveIndicator({
                visible: true,
                top: rect ? Math.max(16, rect.top + Math.min(rect.height / 2, 24)) : 120,
                left: rect ? Math.min(window.innerWidth - 190, rect.right + 12) : window.innerWidth - 200,
                message:
                    customEvent.detail.state === "saving"
                        ? "Autosaving..."
                        : `Saved${customEvent.detail.timestamp ? ` at ${customEvent.detail.timestamp}` : ""}`,
                state: customEvent.detail.state,
            });

            if (customEvent.detail.state === "saved") {
                autoSaveHideTimeoutRef.current = setTimeout(() => {
                    setFieldAutoSaveIndicator((prev) => ({ ...prev, visible: false }));
                }, 2200);
            }
        };

        window.addEventListener("careplan-autosave-status", handleAutoSaveStatus);

        return () => {
            window.removeEventListener("careplan-autosave-status", handleAutoSaveStatus);
            if (autoSaveHideTimeoutRef.current) {
                clearTimeout(autoSaveHideTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!processingAssessment && !assessmentJobDetails) {
            return undefined;
        }

        const intervalId = window.setInterval(() => {
            setAssessmentElapsedTick((value) => value + 1);
        }, 1000);

        return () => {
            window.clearInterval(intervalId);
        };
    }, [assessmentJobDetails, processingAssessment]);

    const submitCarePlanData = async (data: any, options?: { showSuccessToast?: boolean }) => {
        if (!currentFormId) {
            toast.current?.show({ severity: "warn", summary: "Start a Version First", detail: "Create a care plan version before saving sections.", life: 3000 });
            return;
        }

        const carePlanType = String(data?.carePlanType || "").trim();
        if (!carePlanType) return;

        setSavingCarePlan(true);
        try {
            await backendAPIStore.updateNurseNcpForm({
                formId: currentFormId,
                carePlanType,
                sectionData: data,
            });
            setCarePlanSections((prev) => ({ ...prev, [carePlanType]: data }));
            setCurrentFormHeader((prev: any) => prev ? { ...prev, updatedAt: new Date().toISOString() } : prev);
            if (options?.showSuccessToast) {
                toast.current?.show({ severity: "success", summary: "Section Saved", detail: `${capitalizeFirstLetter(carePlanType)} was saved.`, life: 2500 });
            }
        } catch (error: any) {
            toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to save care plan section.", life: 4000 });
        } finally {
            setSavingCarePlan(false);
        }
    };

    const formatAssessmentElapsedTime = (job: any) => {
        void assessmentElapsedTick;

        const startedAt = job?.startedAt || job?.createdAt;
        const finishedAt = job?.completedAt || job?.updatedAt;

        if (!startedAt) {
            return "--";
        }

        const startMs = new Date(startedAt).getTime();
        const isCompleted = String(job?.jobStatus || "").toUpperCase() === JOB_STATUS.COMPLETED;
        const endMs = isCompleted && finishedAt ? new Date(finishedAt).getTime() : Date.now();
        const diffSeconds = Math.max(0, Math.floor((endMs - startMs) / 1000));
        const minutes = Math.floor(diffSeconds / 60);
        const seconds = diffSeconds % 60;

        return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
    };

    const handleCreateVersion = async () => {
        if (!residentId) return;
        setCreatingVersion(true);
        try {
            const created: any = await backendAPIStore.createNurseNcpForm(residentId);
            const nextFormId = String(created?.formId || "").trim();
            await loadForms(nextFormId);
            if (nextFormId) {
                await loadFormRecords(nextFormId);
            }
            toast.current?.show({ severity: "success", summary: "Care Plan Version Ready", detail: `Version ${created?.version || ""} is ready for editing.`, life: 3000 });
        } catch (error: any) {
            toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to start a new care plan version.", life: 4000 });
        } finally {
            setCreatingVersion(false);
        }
    };

    useEffect(() => {
        if (loadingPage || currentFormId || creatingVersion || !residentId) {
            return;
        }
        if (attemptedAutoCreateInitialVersionRef.current) {
            return;
        }

        attemptedAutoCreateInitialVersionRef.current = true;
        void handleCreateVersion();
    }, [creatingVersion, currentFormId, loadingPage, residentId]);

    const saveGeneratedObjectKeyToResidentBinder = async (input: {
        folderCode: string;
        title: string;
        fileName: string;
        objectKey: string;
        mimeType: string;
    }) => {
        if (!residentId) {
            return;
        }

        const bundle = await ensureResidentBinderBundle(backendAPIStore, residentId);
        const targetFolder = getBinderFolderByCode(bundle.folders, input.folderCode);
        if (!bundle.binder?.binderId || !targetFolder?.folderId) {
            throw new Error("Resident binder folder is not available.");
        }

        await upsertBinderDocumentWithVersion({
            backendAPIStore,
            binderId: bundle.binder.binderId,
            folderId: targetFolder.folderId,
            folderName: targetFolder.folderName || input.title,
            referenceId: residentId,
            title: input.title,
            fileName: input.fileName,
            mimeType: input.mimeType,
            existingDocuments: bundle.documents,
            existingObjectKey: input.objectKey,
        });
    };

    const showDocumentActionDialog = (
        title: string,
        message: string,
        onDownload: () => Promise<void>,
        onSaveToBinder: () => Promise<void>,
    ) => {
        confirmDialog({
            message: renderConfirmMessage(title, message),
            header: "Confirmation",
            icon: "pi pi-exclamation-triangle",
            rejectLabel: "Download",
            rejectClassName: "p-button-info p-button-outlined p-button-rounded",
            acceptLabel: "Add to Binder",
            acceptClassName: appConfirmDialogClasses.acceptClassName,
            accept: async () => {
                await onSaveToBinder();
            },
            reject: async () => {
                await onDownload();
            },
        });
    };

    const handleGenerateDocument = async (saveToBinder = false) => {
        if (!residentId || !currentFormId) return;
        setGeneratingDocument(true);
        try {
            const response: any = await backendAPIStore.generateNurseNcpDocument(residentId, currentFormId);
            if (saveToBinder) {
                const outputKey = String(response?.outputKey || "").trim();
                const fileName = String(response?.fileName || "").trim();
                if (!outputKey || !fileName) {
                    throw new Error("Care plan document is missing binder save details.");
                }
                await saveGeneratedObjectKeyToResidentBinder({
                    folderCode: RESIDENT_BINDER_FOLDER_CODES.NEGOTIATED_CARE_PLAN,
                    title: "Negotiated Care Plan",
                    fileName,
                    objectKey: outputKey,
                    mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                });
                toast.current?.show({ severity: "success", summary: "Saved", detail: "Care plan document saved to the resident binder.", life: 3000 });
            } else {
                openUrlInNewTab(response?.documentUrl);
                toast.current?.show({ severity: "success", summary: "Document Ready", detail: response?.fileName || "NCP document generated successfully.", life: 3000 });
            }
        } catch (error: any) {
            if (isSubscriptionRequiredError(error)) {
                openFeatureAccessDialog();
            } else {
                toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to generate the care plan document.", life: 4000 });
            }
        } finally {
            setGeneratingDocument(false);
        }
    };

    const handleGeneratePreliminaryServicePlanDocument = async (saveToBinder = false) => {
        if (!residentId || !currentFormId) return;
        setGeneratingPreliminaryServicePlanDocument(true);
        try {
            const response: any = await backendAPIStore.generateNursePreliminaryServicePlanDocument(residentId, currentFormId);
            if (saveToBinder) {
                const outputKey = String(response?.outputKey || "").trim();
                const fileName = String(response?.fileName || "").trim();
                if (!outputKey || !fileName) {
                    throw new Error("Preliminary service care plan is missing binder save details.");
                }
                await saveGeneratedObjectKeyToResidentBinder({
                    folderCode: RESIDENT_BINDER_FOLDER_CODES.NEGOTIATED_CARE_PLAN,
                    title: "Preliminary Service Care Plan",
                    fileName,
                    objectKey: outputKey,
                    mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                });
                toast.current?.show({
                    severity: "success",
                    summary: "Saved",
                    detail: "Preliminary service care plan saved to the resident binder.",
                    life: 3000,
                });
            } else {
                openUrlInNewTab(response?.documentUrl);
                toast.current?.show({
                    severity: "success",
                    summary: "Document Ready",
                    detail: response?.fileName || "Preliminary service plan document generated successfully.",
                    life: 3000,
                });
            }
        } catch (error: any) {
            if (isSubscriptionRequiredError(error)) {
                openFeatureAccessDialog();
            } else {
                toast.current?.show({
                    severity: "error",
                    summary: getErrorTitle(error),
                    detail: error?.message || "Unable to generate the preliminary service plan document.",
                    life: 4000,
                });
            }
        } finally {
            setGeneratingPreliminaryServicePlanDocument(false);
        }
    };

    const handleGenerateBehaviorInterventionsDocument = async (saveToBinder = false) => {
        if (!residentId || !currentFormId) return;
        setGeneratingBehaviorInterventionsDocument(true);
        try {
            const response: any = await backendAPIStore.generateNurseBehaviorInterventionsDocument(residentId, currentFormId);
            if (saveToBinder) {
                const outputKey = String(response?.outputKey || "").trim();
                const fileName = String(response?.fileName || "").trim();
                if (!outputKey || !fileName) {
                    throw new Error("Behavior and interventions care plan is missing binder save details.");
                }
                await saveGeneratedObjectKeyToResidentBinder({
                    folderCode: RESIDENT_BINDER_FOLDER_CODES.NEGOTIATED_CARE_PLAN,
                    title: "Behavior and Interventions Care Plan",
                    fileName,
                    objectKey: outputKey,
                    mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                });
                toast.current?.show({
                    severity: "success",
                    summary: "Saved",
                    detail: "Behavior and interventions care plan saved to the resident binder.",
                    life: 3000,
                });
            } else {
                openUrlInNewTab(response?.documentUrl);
                toast.current?.show({
                    severity: "success",
                    summary: "Document Ready",
                    detail: response?.fileName || "Behavior and interventions care plan generated successfully.",
                    life: 3000,
                });
            }
        } catch (error: any) {
            if (isSubscriptionRequiredError(error)) {
                openFeatureAccessDialog();
            } else {
                toast.current?.show({
                    severity: "error",
                    summary: getErrorTitle(error),
                    detail: error?.message || "Unable to generate the behavior and interventions care plan.",
                    life: 4000,
                });
            }
        } finally {
            setGeneratingBehaviorInterventionsDocument(false);
        }
    };

    const runNurseNcpImportJob = async (objectKey: string, useExistingCredit = false) => {
        if (!residentId || !currentFormId) return;
        if (hasActiveNcpImportJob) {
            toast.current?.show({
                severity: "warn",
                summary: "Draft already running",
                detail: "A care plan draft job is already running for this resident. Wait for it to finish before starting another draft.",
                life: 4000,
            });
            return;
        }

        setAssessmentDialogError(null);
        setProcessingAssessment(true);
        setAssessmentJobDetails(null);
        try {
            await backendAPIStore.init();
            const jobDetails: any = await backendAPIStore.startNurseNcpImportJob({
                residentId,
                formId: currentFormId,
                objectKey,
                billingRoleType: (isProviderNurseResidentMode || isSharedResidentMode) ? ROLE_TYPE_PROVIDER : ROLE_TYPE_NURSE,
                useExistingCredit,
            });

            let currentJob = await backendAPIStore.getJobDetails(jobDetails.jobId);
            setAssessmentJobDetails(currentJob);
            let jobStatus = String(currentJob?.jobStatus || JOB_STATUS.SUBMITTED);
            const startedAt = Date.now();

            while (
                jobStatus !== JOB_STATUS.COMPLETED
                && jobStatus !== JOB_STATUS.FAILED
                && (Date.now() - startedAt) / 1000 < 300
            ) {
                await new Promise((resolve) => setTimeout(resolve, 5000));
                currentJob = await backendAPIStore.getJobDetails(jobDetails.jobId);
                setAssessmentJobDetails(currentJob);
                jobStatus = String(currentJob?.jobStatus || JOB_STATUS.SUBMITTED);
            }

            if (jobStatus === JOB_STATUS.COMPLETED) {
                await loadForms(currentFormId);
                await loadFormRecords(currentFormId);
                await loadResidentBinderData();
                toast.current?.show({
                    severity: "success",
                    summary: "Care Plan Draft Ready",
                    detail: "The imported assessment was used to refresh this nurse care plan version.",
                    life: 3500,
                });
            } else {
                throw new Error(currentJob?.errorMessage || "There was an issue processing the assessment import.");
            }
        } catch (error: any) {
            const code = String(error?.code || error?.details?.code || "").trim().toUpperCase();
            if (code === "NCP_JOB_ALREADY_RUNNING") {
                setAssessmentJobDetails({
                    jobId: error?.currentJobId || null,
                    jobStatus: error?.currentJobStatus || JOB_STATUS.SUBMITTED,
                });
                toast.current?.show({
                    severity: "warn",
                    summary: "Draft already running",
                    detail: error?.message || "A care plan draft job is already running for this resident.",
                    life: 4500,
                });
            } else if (isSubscriptionRequiredError(error)) {
                setAssessmentJobDetails(null);
                openFeatureAccessDialog({
                    allowCreditUse: !useExistingCredit,
                    onUseCredit: () => { void runNurseNcpImportJob(objectKey, true); },
                });
            } else {
                setAssessmentDialogError({
                    summary: getErrorTitle(error),
                    detail: error?.message || "Unable to import the assessment into this care plan.",
                });
                toast.current?.show({
                    severity: "error",
                    summary: getErrorTitle(error),
                    detail: error?.message || "Unable to import the assessment into this care plan.",
                    life: 4500,
                });
            }
        } finally {
            setProcessingAssessment(false);
            setUploadingAssessment(false);
        }
    };

    const importExistingBinderAssessment = async () => {
        if (!selectedBinderAssessmentDocumentId) {
            toast.current?.show({ severity: "warn", summary: "Assessment required", detail: "Choose an assessment from the resident binder first.", life: 3500 });
            return;
        }

        const selectedDocument = residentBinderDocuments.find((document) => document.binderDocumentId === selectedBinderAssessmentDocumentId);
        if (!selectedDocument?.binderDocumentId) {
            toast.current?.show({ severity: "error", summary: "Assessment not found", detail: "The selected binder assessment could not be found.", life: 4000 });
            return;
        }

        setUploadingAssessment(true);
        setAssessmentDialogError(null);
        try {
            const latestVersion = await getLatestBinderDocumentVersion(backendAPIStore, selectedDocument.binderDocumentId);
            if (!latestVersion?.s3Key) {
                throw new Error("The selected binder assessment does not have a latest version yet.");
            }

            await runNurseNcpImportJob(latestVersion.s3Key);
        } catch (error: any) {
            setAssessmentDialogError({
                summary: getErrorTitle(error),
                detail: error?.message || "Unable to use the selected binder assessment.",
            });
            toast.current?.show({
                severity: "error",
                summary: getErrorTitle(error),
                detail: error?.message || "Unable to use the selected binder assessment.",
                life: 4500,
            });
            setUploadingAssessment(false);
            setProcessingAssessment(false);
        }
    };

    const uploadAssessmentFile = async () => {
        if (!residentId || !selectedAssessmentFile) {
            toast.current?.show({ severity: "warn", summary: "No file selected", detail: "Choose an assessment file first.", life: 3500 });
            return;
        }

        setUploadingAssessment(true);
        setAssessmentDialogError(null);
        try {
            const bundle = residentBinder?.binderId
                ? { binder: residentBinder, folders: residentBinderFolders, documents: residentBinderDocuments }
                : await ensureResidentBinderBundle(backendAPIStore, residentId);
            const assessmentFolder = getBinderFolderByCode(bundle.folders, RESIDENT_BINDER_FOLDER_CODES.ASSESSMENT);
            if (!bundle.binder?.binderId || !assessmentFolder?.folderId) {
                throw new Error("Resident assessment binder folder is not available.");
            }

            const createdDocument = await uploadNewBinderDocument({
                backendAPIStore,
                binderId: bundle.binder.binderId,
                folderId: assessmentFolder.folderId,
                folderName: String(assessmentFolder.folderName || "Assessment"),
                referenceId: residentId,
                title: selectedAssessmentFile.name,
                file: selectedAssessmentFile,
                isPrimaryDocument: true,
            });

            setSelectedBinderAssessmentDocumentId(createdDocument?.binderDocumentId || "");
            await loadResidentBinderData();
            const latestVersion = await getLatestBinderDocumentVersion(backendAPIStore, createdDocument?.binderDocumentId);
            if (!latestVersion?.s3Key) {
                throw new Error("The uploaded assessment could not be saved to the resident binder.");
            }

            await runNurseNcpImportJob(latestVersion.s3Key);
            setSelectedAssessmentFile(null);
        } catch (error: any) {
            setAssessmentDialogError({
                summary: getErrorTitle(error),
                detail: error?.message || "Unable to upload the assessment into the resident binder.",
            });
            toast.current?.show({
                severity: "error",
                summary: getErrorTitle(error),
                detail: error?.message || "Unable to upload the assessment into the resident binder.",
                life: 4500,
            });
            setUploadingAssessment(false);
            setProcessingAssessment(false);
        }
    };

    const handleFileChange = (event: any) => {
        setAssessmentDialogError(null);
        const file = event?.files?.[0];
        if (!file) {
            setSelectedAssessmentFile(null);
            return;
        }

        const extension = String(file.name || "").split(".").pop()?.toLowerCase() || "";
        if (file.size > RESIDENT_ASSESSMENT_MAX_FILE_SIZE_IN_BYTES || !ASSESSMENT_UPLOAD_ALLOWED_EXTENSIONS.includes(extension)) {
            toast.current?.show({
                severity: "error",
                summary: "Invalid file",
                detail: `Please upload a PDF, DOC, or DOCX file smaller than ${RESIDENT_ASSESSMENT_MAX_FILE_SIZE_IN_BYTES / (1024 * 1024)} MB.`,
                life: 4500,
            });
            setSelectedAssessmentFile(null);
            return;
        }

        setSelectedAssessmentFile(file);
    };

    const nextCallback = () => setActiveStep((prev) => Math.min((prev ?? 0) + 1, NCP_STEPS.length - 1));
    const prevCallback = () => setActiveStep((prev) => Math.max((prev ?? 0) - 1, 0));
    const showHelpMessage = (message: string) => {
        setHelpMessage(message);
        setShowHelpDialog(true);
    };

    const handleCloseAssessmentImportDialog = () => {
        setShowAssessmentImportDialog(false);
        setAssessmentDialogError(null);
        if (String(assessmentJobDetails?.jobStatus || "").toUpperCase() === JOB_STATUS.COMPLETED && typeof window !== "undefined") {
            window.setTimeout(() => {
                window.location.reload();
            }, 0);
        }
    };

    const openAssessmentImportDialog = () => {
        if (!processingAssessment && !hasActiveNcpImportJob) {
            setAssessmentJobDetails(null);
            setSelectedAssessmentFile(null);
        }
        setAssessmentDialogError(null);
        setShowAssessmentImportDialog(true);
    };

    const normalizedHelpMessage = helpMessage.replace(/\\n/g, "\n");

    const sectionComponents = useMemo(() => ([
        <SummarySection
            key="summary"
            sectionData={{
                ...carePlanSections[RESIDENT_CARE_PLAN_TYPE.SUMMARY],
                acesId: carePlanSections[RESIDENT_CARE_PLAN_TYPE.SUMMARY]?.acesId || "",
            }}
            submitButtonClick={submitCarePlanData}
            loadingCarePlan={savingCarePlan}
            nextButtonClick={nextCallback}
            showHelpMessage={showHelpMessage}
        />,
        <ResponsiblePartiesSection key="responsible" sectionData={carePlanSections[RESIDENT_CARE_PLAN_TYPE.RESPONSIBLE_PARTIES]} submitButtonClick={submitCarePlanData} loadingCarePlan={savingCarePlan} backButtonClick={prevCallback} nextButtonClick={nextCallback} showHelpMessage={showHelpMessage} />,
        <CommunicationSection key="communication" sectionData={carePlanSections[RESIDENT_CARE_PLAN_TYPE.COMMUNICATION]} submitButtonClick={submitCarePlanData} loadingCarePlan={savingCarePlan} backButtonClick={prevCallback} nextButtonClick={nextCallback} showHelpMessage={showHelpMessage} />,
        <MedicationManagementSection key="medication" sectionData={carePlanSections[RESIDENT_CARE_PLAN_TYPE.MEDICATION_MANAGEMENT]} submitButtonClick={submitCarePlanData} loadingCarePlan={savingCarePlan} backButtonClick={prevCallback} nextButtonClick={nextCallback} showHelpMessage={showHelpMessage} />,
        <HealthIndicatorSection key="health" sectionData={carePlanSections[RESIDENT_CARE_PLAN_TYPE.HEALTH_INDICATORS]} submitButtonClick={submitCarePlanData} loadingCarePlan={savingCarePlan} backButtonClick={prevCallback} nextButtonClick={nextCallback} showHelpMessage={showHelpMessage} />,
        <BodyChartSection key="body-chart" sectionData={carePlanSections[BODY_CHART_CARE_PLAN_TYPE]} submitButtonClick={submitCarePlanData} loadingCarePlan={savingCarePlan} backButtonClick={prevCallback} nextButtonClick={nextCallback} contextNote="NCP - Body Chart will be included in the Preliminary Care Plan document only" />,
        <TreatmentProgramSection key="treatment" sectionData={carePlanSections[RESIDENT_CARE_PLAN_TYPE.TREATMENT_PROGRAM_THERAPIES]} submitButtonClick={submitCarePlanData} loadingCarePlan={savingCarePlan} backButtonClick={prevCallback} nextButtonClick={nextCallback} showHelpMessage={showHelpMessage} />,
        <PsychSocialSection key="psych" sectionData={carePlanSections[RESIDENT_CARE_PLAN_TYPE.PSYCH_SOCIAL_COGNITIVE]} submitButtonClick={submitCarePlanData} loadingCarePlan={savingCarePlan} backButtonClick={prevCallback} nextButtonClick={nextCallback} showHelpMessage={showHelpMessage} />,
        <LeftAloneSection key="leftalone" sectionData={carePlanSections[RESIDENT_CARE_PLAN_TYPE.LEFT_ALONE_STATUS]} submitButtonClick={submitCarePlanData} loadingCarePlan={savingCarePlan} backButtonClick={prevCallback} nextButtonClick={nextCallback} showHelpMessage={showHelpMessage} />,
        <UniversalPrecautionSection key="universal" sectionData={carePlanSections[RESIDENT_CARE_PLAN_TYPE.UNIVERSAL_PRECAUTIONS]} submitButtonClick={submitCarePlanData} loadingCarePlan={savingCarePlan} backButtonClick={prevCallback} nextButtonClick={nextCallback} showHelpMessage={showHelpMessage} />,
        <ActivitiesOfDailyLivingSection key="adl" sectionData={carePlanSections[RESIDENT_CARE_PLAN_TYPE.ACTIVITIES_OF_DAILY_LIVING]} submitButtonClick={submitCarePlanData} loadingCarePlan={savingCarePlan} backButtonClick={prevCallback} nextButtonClick={nextCallback} showHelpMessage={showHelpMessage} />,
        <InstrumentalActivitiesSection key="iadl" sectionData={carePlanSections[RESIDENT_CARE_PLAN_TYPE.INSTRUMENTAL_ACTIVITIES_OF_DAILY_LIVING]} submitButtonClick={submitCarePlanData} loadingCarePlan={savingCarePlan} backButtonClick={prevCallback} nextButtonClick={nextCallback} showHelpMessage={showHelpMessage} />,
    ]), [carePlanSections, savingCarePlan]);
    const assessmentBinderFolder = getBinderFolderByCode(residentBinderFolders, RESIDENT_BINDER_FOLDER_CODES.ASSESSMENT);
    const assessmentBinderDocuments = getFolderDocuments(residentBinderDocuments, assessmentBinderFolder?.folderId);
    const residentBinderFoldersWithFiles = residentBinderFolders.filter((folder) => getFolderDocuments(residentBinderDocuments, folder.folderId).length > 0);
    const selectedBinderFolderDocuments = getFolderDocuments(residentBinderDocuments, selectedBinderFolderId);
    const hasImportedAssessment = Boolean(currentFormHeader?.importedAssessmentOutputKey);
    const isAssessmentJobCompleted = String(assessmentJobDetails?.jobStatus || "").toUpperCase() === JOB_STATUS.COMPLETED;
    const isCurrentFormImportCompleted = String(currentFormHeader?.jobStatus || "").toUpperCase() === JOB_STATUS.COMPLETED;
    const isAssessmentReadyForCarePlan = hasImportedAssessment || isAssessmentJobCompleted || isCurrentFormImportCompleted;
    const formatBinderDocumentOptionLabel = (binderDocument: BinderDocument) => {
        const modifiedAt = String((binderDocument as any)?.modifiedAt || binderDocument?.createdAt || "").trim();
        const suffix = modifiedAt ? ` • ${new Date(modifiedAt).toLocaleDateString()}` : "";
        return `${binderDocument?.title || "Assessment"}${binderDocument?.isPrimaryDocument ? " • Primary" : ""}${suffix}`;
    };
    const renderWorkflowSection = () => {
        const workflowActions = [
            {
                icon: "pi pi-upload",
                label: "Choose or Upload Assessment",
                buttonLabel: "Upload",
                loading: uploadingAssessment || processingAssessment,
                disabled: hasActiveNcpImportJob,
                action: openAssessmentImportDialog,
            },
            {
                icon: "pi pi-file-edit",
                label: "Negotiated Care Plan",
                buttonLabel: "Download",
                loading: generatingDocument,
                disabled: false,
                action: () => showDocumentActionDialog(
                    "Generate care plan document",
                    "Choose whether to download the care plan document or save it directly to the resident binder.",
                    async () => { await handleGenerateDocument(false); },
                    async () => { await handleGenerateDocument(true); },
                ),
            },
            {
                icon: "pi pi-book",
                label: "Behavior and Interventions Care Plan",
                buttonLabel: "Download",
                loading: generatingBehaviorInterventionsDocument,
                disabled: false,
                action: () => showDocumentActionDialog(
                    "Generate behavior and interventions care plan",
                    "Choose whether to download the behavior and interventions care plan or add it directly to the resident binder.",
                    async () => { await handleGenerateBehaviorInterventionsDocument(false); },
                    async () => { await handleGenerateBehaviorInterventionsDocument(true); },
                ),
            },
            {
                icon: "pi pi-clipboard",
                label: "Preliminary Service Care Plan",
                buttonLabel: "Download",
                loading: generatingPreliminaryServicePlanDocument,
                disabled: false,
                action: () => showDocumentActionDialog(
                    "Generate preliminary service care plan",
                    "Choose whether to download the preliminary service care plan or add it directly to the resident binder.",
                    async () => { await handleGeneratePreliminaryServicePlanDocument(false); },
                    async () => { await handleGeneratePreliminaryServicePlanDocument(true); },
                ),
            },
        ];
        return (
            <div style={appNeutralPanelStyle} className="resident-careplan-actions">
                <div className="resident-careplan-actions-grid">
                    <div className="resident-careplan-actions-info">
                        <div className="grid mb-3">
                            <div className="col-12 lg:col-8">
                                <div>
                                    <div style={{ fontWeight: 700, color: "#0f172a" }}>Care Plan Version Workspace</div>
                                    <div className="mt-2" style={{ color: "#64748b", lineHeight: 1.7 }}>
                                        Use this page to build, review, and complete the current nurse care plan version for this resident.
                                    </div>
                                </div>
                                <div className="mt-3 flex flex-column gap-2" style={{ color: "#64748b", lineHeight: 1.7 }}>
                                    <div>Import an assessment from the resident binder, or upload a new one and use it right away to prepare a draft care plan.</div>
                                    <div>Once the assessment is imported, the care plan documents and related planning tools become available for this version.</div>
                                </div>
                            </div>
                            <div className="col-12 lg:col-4 flex lg:justify-content-end lg:align-items-start">
                                <div
                                    style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: "0.5rem",
                                        borderRadius: "999px",
                                        padding: "0.5rem 0.9rem",
                                        background: isAssessmentReadyForCarePlan ? "#ecfdf3" : "#fff7ed",
                                        color: isAssessmentReadyForCarePlan ? "#047857" : "#b45309",
                                        fontWeight: 700,
                                    }}
                                >
                                    <i className={`pi ${isAssessmentReadyForCarePlan ? "pi-check-circle" : "pi-clock"}`} />
                                    <span>{isAssessmentReadyForCarePlan ? "Assessment imported" : "Assessment is required"}</span>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-2 align-items-center" style={{ marginTop: "0.5rem" }}>
                            <Tag value={`VERSION ${currentFormHeader?.version || 1}`} severity="info" rounded />
                            <Tag value={String(currentFormHeader?.status || "draft").toUpperCase()} severity={String(currentFormHeader?.status || "").toLowerCase() === "completed" ? "success" : "warning"} rounded />
                            <span style={{ color: "#64748b", fontWeight: 600 }}>
                                {currentFormHeader?.updatedAt ? `Last saved ${new Date(currentFormHeader.updatedAt).toLocaleString()}` : "Not yet saved"}
                            </span>
                        </div>
                    </div>
                    <div className="resident-careplan-action-list">
                        <ul className="list-none m-0 p-0">
                            {workflowActions.map((item) => (
                                <li
                                    key={item.label}
                                    className="flex flex-wrap align-items-center justify-content-between gap-3 py-3 px-3 resident-careplan-action-item"
                                >
                                    <span className={`flex align-items-center mr-2 gap-2${item.disabled ? " text-400" : ""}`} style={{ fontWeight: 600, color: "#0f172a" }}>
                                        <i className={`${item.icon} text-primary`} />
                                        <span>{item.label}</span>
                                    </span>
                                    <Button
                                        label={item.buttonLabel}
                                        severity="warning"
                                        rounded
                                        outlined
                                        size="small"
                                        disabled={item.disabled}
                                        loading={item.loading}
                                        onClick={item.action}
                                    />
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        );
    };

    const profileFooterTemplate = () => (
        isProviderNurseResidentMode ? (
            <div className="flex flex-wrap justify-content-end gap-2">
                <Button
                    icon="pi pi-folder-open"
                    outlined
                    rounded
                    label="Binder"
                    severity="warning"
                    size="small"
                    disabled={sharedResidentBinderAccessDenied}
                    onClick={() => navigate(binderPath)}
                />
            </div>
        ) : (
            <div className="flex gap-2 flex-wrap align-items-center">
                <Button type="button" label="Back to Resident" severity="warning" rounded outlined onClick={() => navigate(backToResidentPath)} />
                <Button type="button" label="Binder" severity="warning" rounded outlined onClick={() => navigate(binderPath)} />
            </div>
        )
    );

    return (
        <div style={{ padding: "1.25rem" }}>
            <Toast ref={toast} />
            {fieldAutoSaveIndicator.visible ? (
                <div
                    style={{
                        position: "fixed",
                        top: fieldAutoSaveIndicator.top,
                        left: fieldAutoSaveIndicator.left,
                        zIndex: 1300,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        padding: "0.55rem 0.8rem",
                        borderRadius: "999px",
                        background: fieldAutoSaveIndicator.state === "saving" ? "#dbeafe" : "#ecfdf3",
                        color: fieldAutoSaveIndicator.state === "saving" ? "#1d4ed8" : "#047857",
                        border: `1px solid ${fieldAutoSaveIndicator.state === "saving" ? "#93c5fd" : "#a7f3d0"}`,
                        boxShadow: "0 10px 24px rgba(15, 23, 42, 0.12)",
                        fontWeight: 700,
                        fontSize: "0.82rem",
                        pointerEvents: "none",
                    }}
                >
                    <i className={`pi ${fieldAutoSaveIndicator.state === "saving" ? "pi-spin pi-spinner" : "pi-check-circle"}`} />
                    <span>{fieldAutoSaveIndicator.message}</span>
                </div>
            ) : null}
            <div style={dashboardPageContainerStyle}>
                {renderDashboardHeader(
                    isProviderNurseResidentMode
                        ? (residentProfile?.firstName || residentProfile?.lastName
                            ? `${`${residentProfile?.firstName || ""} ${residentProfile?.lastName || ""}`.trim()} Care Plan`
                            : "Resident Care Plan")
                        : `Negotiated Care Plan${residentProfile?.firstName || residentProfile?.lastName ? ` - ${`${residentProfile?.firstName || ""} ${residentProfile?.lastName || ""}`.trim()}` : ""}`,
                    isProviderNurseResidentMode
                        ? "Upload the assessment, create the care plan draft, and continue with the related planning documents."
                        : "Work on versioned nurse care plans separately from the assessment workspace.",
                    profileFooterTemplate(),
                )}

                {loadingPage ? (
                    <div className="flex justify-content-center" style={{ padding: "2rem" }}>
                        <ProgressSpinner />
                    </div>
                ) : !currentFormId ? (
                    <Card style={appSectionCardStyle}>
                        <div className="flex flex-column align-items-center justify-content-center" style={{ padding: "1.5rem", gap: "0.85rem" }}>
                            <ProgressSpinner style={{ width: "3rem", height: "3rem" }} strokeWidth="5" />
                            <div style={{ fontWeight: 700, color: "#0f172a" }}>
                                Preparing the first care plan version...
                            </div>
                        </div>
                    </Card>
                ) : (
                    <>
                        <div className={isProviderNurseResidentMode ? "resident-careplan-layout" : undefined}>
                            {renderWorkflowSection()}

                            <Card style={appSectionCardStyle}>
                                {renderSurfaceHeader(
                                    "Care Plan Sections",
                                    "Fill in each care plan section. Changes are saved to the current nurse care plan version.",
                                    "pi pi-file-edit",
                                )}
                                <div className="p-3">
                                    <div style={{ fontWeight: 700, color: "#0f172a", display: "block", width: "100%", margin: "0 0 0.75rem 0" }}>
                                        Expand each Assessment & Care Plan Sections (For Editing and Review)
                                    </div>
                                    <Accordion activeIndex={activeStep ?? undefined} onTabChange={(e) => setActiveStep(e.index as number | null)}>
                                        {NCP_STEPS.map((step, index) => (
                                            <AccordionTab key={step.label} header={renderAccordionHeader(index, step.label, step.icon, activeStep)}>
                                                {sectionComponents[index]}
                                            </AccordionTab>
                                        ))}
                                    </Accordion>
                                </div>
                            </Card>
                        </div>
                    </>
                )}
            </div>

            <Dialog
                header={renderSurfaceHeader("Assessment to Care Plan Automation", "", "pi pi-sparkles")}
                visible={showAssessmentImportDialog}
                style={appDialogStyle}
                headerStyle={appDialogHeaderStyle}
                contentStyle={appDialogContentStyle}
                modal
                onHide={handleCloseAssessmentImportDialog}
                footer={(
                    <p style={{ margin: 0, fontSize: "0.82rem", color: "#64748b", fontStyle: "italic" }}>
                        Assessment import refreshes the current nurse care plan version with a new draft. Review every section before finalizing.
                    </p>
                )}
            >
                <div className="flex flex-column gap-4">
                    {assessmentDialogError ? (
                        <Message
                            severity="error"
                            text={`${assessmentDialogError.summary}: ${assessmentDialogError.detail}`}
                        />
                    ) : null}

                    {!processingAssessment && !assessmentJobDetails ? (
                        <>
                            <div
                                style={{
                                    border: "1px solid #dbe7f3",
                                    borderRadius: "14px",
                                    background: "linear-gradient(180deg, #f8fbff 0%, #ffffff 100%)",
                                    padding: "0.9rem 1rem",
                                }}
                            >
                                <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.25rem" }}>
                                    Start with an assessment or NCP
                                </div>
                                <div style={{ color: "#64748b", lineHeight: 1.6 }}>
                                    {residentBinderFoldersWithFiles.length
                                        ? "Choose an assessment or NCP already in the resident binder, or upload a new one below to generate the care plan draft."
                                        : "Upload a new assessment or NCP below to generate the care plan draft. It will be saved to the resident binder automatically."}
                                </div>
                            </div>

                            {sharedResidentBinderAccessDenied ? (
                                <Message
                                    severity="info"
                                    text="Binder access is not enabled for this shared resident. Binder-based import options are unavailable."
                                />
                            ) : null}

                            {hasActiveNcpImportJob ? (
                                <div
                                    style={{
                                        background: "#fff7ed",
                                        border: "1px solid #fdba74",
                                        borderRadius: "12px",
                                        padding: "0.85rem 1rem",
                                        color: "#9a3412",
                                        fontWeight: 600,
                                    }}
                                >
                                    A care plan draft job is already running for this resident. Wait for it to finish before starting another draft.
                                </div>
                            ) : null}

                            {residentBinderFoldersWithFiles.length && !sharedResidentBinderAccessDenied ? (
                                <div className="flex flex-column gap-3">
                                    <div style={{ fontWeight: 700, color: "#0f172a" }}>Use an assessment or NCP already in the resident binder</div>
                                    <div className="grid">
                                        <div className="col-12 md:col-6 flex flex-column gap-2">
                                            <label htmlFor="nurse-ncp-binder-folder" className="font-semibold text-900">Binder folder</label>
                                            <Dropdown
                                        inputId="nurse-ncp-binder-folder"
                                        value={selectedBinderFolderId}
                                        options={residentBinderFoldersWithFiles}
                                        optionLabel="folderName"
                                        optionValue="folderId"
                                        onChange={(e) => {
                                            const folderId = String(e.value || "");
                                            const files = getFolderDocuments(residentBinderDocuments, folderId);
                                            setSelectedBinderFolderId(folderId);
                                            setSelectedBinderAssessmentDocumentId(files.length === 1 ? files[0].binderDocumentId || "" : "");
                                        }}
                                        placeholder="Choose a Binder folder"
                                        style={{ width: "100%" }}
                                                emptyMessage="No Binder folders found."
                                            />
                                        </div>
                                        <div className="col-12 md:col-6 flex flex-column gap-2">
                                            <label htmlFor="nurse-ncp-binder-file" className="font-semibold text-900">Binder file</label>
                                            <Dropdown
                                        inputId="nurse-ncp-binder-file"
                                        value={selectedBinderAssessmentDocumentId}
                                        options={selectedBinderFolderDocuments}
                                        optionLabel="title"
                                        optionValue="binderDocumentId"
                                        onChange={(e) => setSelectedBinderAssessmentDocumentId(String(e.value || ""))}
                                        placeholder="Choose a file from the selected folder"
                                        style={{ width: "100%" }}
                                        itemTemplate={(option: BinderDocument) => <span>{formatBinderDocumentOptionLabel(option)}</span>}
                                        valueTemplate={(option: BinderDocument | null) =>
                                            option ? <span>{formatBinderDocumentOptionLabel(option)}</span> : <span>Choose a file from the selected folder</span>
                                        }
                                                emptyMessage="No files found in the selected folder."
                                            />
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap align-items-center gap-2">
                                        <Button
                                            label="Use Binder File"
                                            severity="warning"
                                            rounded
                                            outlined
                                            disabled={!selectedBinderAssessmentDocumentId || uploadingAssessment || processingAssessment || hasActiveNcpImportJob}
                                            onClick={() => { void importExistingBinderAssessment(); }}
                                        />
                                    </div>
                                    <div style={{ borderTop: "1px solid #e2e8f0", paddingTop: "1rem", color: "#64748b", fontWeight: 600 }}>
                                        Or upload a new assessment to the binder
                                    </div>
                                </div>
                            ) : null}

                            <FileUpload
                                mode="advanced"
                                name="nurseNcpAssessment"
                                accept={ASSESSMENT_UPLOAD_ACCEPT}
                                maxFileSize={RESIDENT_ASSESSMENT_MAX_FILE_SIZE_IN_BYTES}
                                customUpload
                                auto={false}
                                onSelect={handleFileChange}
                                uploadHandler={() => { void uploadAssessmentFile(); }}
                                disabled={uploadingAssessment || processingAssessment || hasActiveNcpImportJob}
                                chooseOptions={chooseOptions}
                                uploadOptions={uploadOptions}
                                cancelOptions={NCP_CANCEL_OPTIONS}
                                emptyTemplate={<p className="m-0">Upload or drag and drop a PDF, DOC, or DOCX assessment here. It will be saved into the resident binder and used for this import.</p>}
                            />
                        </>
                    ) : null}

                    {uploadingAssessment || processingAssessment ? (
                        <div className="flex flex-column align-items-center justify-content-center" style={{ padding: "0.5rem 0 0.25rem" }}>
                            <ProgressSpinner />
                            <div style={{ marginTop: "0.75rem", color: "#475569", fontWeight: 600 }}>
                                {uploadingAssessment ? "Uploading assessment..." : "Processing assessment import..."}
                            </div>
                            {processingAssessment && assessmentJobDetails ? (
                                <div style={{ marginTop: "0.35rem", color: "#64748b", fontSize: "0.9rem" }}>
                                    Elapsed: {formatAssessmentElapsedTime(assessmentJobDetails)}
                                </div>
                            ) : null}
                            {processingAssessment && assessmentJobDetails?.jobSubStatus ? (
                                <div style={{ marginTop: "0.45rem", color: "#64748b" }}>
                                    {String(assessmentJobDetails.jobSubStatus)}
                                </div>
                            ) : null}
                        </div>
                    ) : null}

                    {!processingAssessment && !assessmentJobDetails ? (
                        <div style={{ color: "#64748b", fontStyle: "italic" }}>
                            Current care plan section data will be overwritten after assessment processing. Download or save a document to the binder first if you need a backup.
                        </div>
                    ) : null}

                    {assessmentJobDetails && !processingAssessment && String(assessmentJobDetails?.jobStatus || "").toUpperCase() === JOB_STATUS.COMPLETED ? (
                        <div className="flex flex-column gap-3">
                            <div style={{
                                background: "#fef3c7",
                                border: "1.5px solid #fcd34d",
                                borderRadius: "10px",
                                padding: "0.9rem 1.1rem",
                                display: "flex",
                                alignItems: "flex-start",
                                gap: "0.7rem",
                            }}>
                                <i className="pi pi-exclamation-triangle" style={{ color: "#d97706", fontSize: "1.3rem", marginTop: "0.1rem", flexShrink: 0 }} />
                                <div>
                                    <div style={{ fontWeight: 700, color: "#92400e", fontSize: "0.95rem", marginBottom: "0.3rem" }}>
                                        Care Plan Draft Ready
                                    </div>
                                    <div style={{ color: "#78350f", fontSize: "0.88rem", lineHeight: 1.6 }}>
                                        Review the care plan, add resident-specific caregiver interventions, and verify the clinical details before using it as final.
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : null}
                </div>
            </Dialog>

            <Dialog
                header={renderSurfaceHeader("More Access Needed", "", "pi pi-wallet")}
                visible={showFeatureAccessDialog}
                onHide={() => {
                    setShowFeatureAccessDialog(false);
                    setFeatureAccessDialogAllowsCreditUse(false);
                    featureAccessRetryActionRef.current = null;
                    setProcessingAssessment(false);
                    setUploadingAssessment(false);
                }}
                style={appDialogStyle}
                headerStyle={appDialogHeaderStyle}
                contentStyle={appDialogContentStyle}
                modal
            >
                <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                    <div style={{ color: "#596F80", lineHeight: 1.7, whiteSpace: "pre-line" }}>
                        {featureAccessDialogMessage}
                    </div>
                    {!requiresProviderSubscriptionMessageOnly ? (
                        <>
                            <div style={{ background: "linear-gradient(180deg, #fffaf0 0%, #ffffff 100%)", border: "1px solid #fed7aa", borderRadius: 14, padding: "1rem" }}>
                                <div style={{ fontWeight: 700, color: "#9a3412" }}>Next</div>
                                <div className="mt-2" style={{ color: "#596F80", lineHeight: 1.7 }}>
                                    {featureAccessDialogAllowsCreditUse
                                        ? `You currently have ${automationCreditsAvailable} automation credit${automationCreditsAvailable === 1 ? "" : "s"} available. Use 1 credit to continue now.`
                                        : isUserSubscribed
                                        ? "Buy automation credits in Billing."
                                        : "Open Billing to subscribe or buy automation credits."}
                                </div>
                            </div>
                            <div className="flex flex-wrap justify-content-end gap-2">
                                {!isUserSubscribed ? (
                                    <Button
                                        type="button"
                                        label="Subscription"
                                        severity="secondary"
                                        outlined
                                        rounded
                                        onClick={() => {
                                            setShowFeatureAccessDialog(false);
                                            navigate(`${RouterAddress.BILLING}?tab=subscription`);
                                        }}
                                    />
                                ) : null}
                                {featureAccessDialogAllowsCreditUse ? (
                                    <Button
                                        type="button"
                                        label="Use 1 Automation Credit"
                                        severity="warning"
                                        rounded
                                        onClick={() => {
                                            const retryAction = featureAccessRetryActionRef.current;
                                            setShowFeatureAccessDialog(false);
                                            setFeatureAccessDialogAllowsCreditUse(false);
                                            featureAccessRetryActionRef.current = null;
                                            retryAction?.();
                                        }}
                                    />
                                ) : (
                                    <Button
                                        type="button"
                                        label="Buy Automation Credits"
                                        severity="warning"
                                        rounded
                                        onClick={() => {
                                            setShowFeatureAccessDialog(false);
                                            navigate(`${RouterAddress.BILLING}?tab=credits`);
                                        }}
                                    />
                                )}
                            </div>
                        </>
                    ) : null}
                </div>
            </Dialog>

            <Dialog
                header={renderSurfaceHeader("DSHS Guidance", "Helpful context for a specific care-plan field.", "pi pi-info-circle")}
                visible={showHelpDialog}
                style={appDialogStyle}
                headerStyle={appDialogHeaderStyle}
                contentStyle={appDialogContentStyle}
                modal
                onHide={() => setShowHelpDialog(false)}
            >
                {/[<][a-z/!]/i.test(normalizedHelpMessage) ? (
                    <>
                        <style>{nurseNcpHelpHtmlStyle}</style>
                        <div
                            className="nurse-ncp-help-html"
                            dangerouslySetInnerHTML={{
                                __html: sanitizeRichHtml(normalizedHelpMessage).replace(/\n/g, "<br />"),
                            }}
                        />
                    </>
                ) : (
                    <div style={{ whiteSpace: "pre-line", color: "#475569", lineHeight: 1.7 }}>{normalizedHelpMessage}</div>
                )}
            </Dialog>
        </div>
    );
};

export default NurseResidentNcpPage;
